import type { GameStatus, Milestone, Scenario, ScenarioObjective } from '../types';

const STORAGE_PREFIX = 'compliance_command_leaderboard_';
export const LEADERBOARD_MAX_ENTRIES = 10;

export type LeaderboardOutcome = Extract<GameStatus, 'won' | 'lost' | 'scenario_won'>;

export interface LeaderboardEntry {
  score: number;
  date: string;
  playerLabel: 'You';
  compliance: number;
  budget: number;
  threat: number;
  day: number;
  outcome: LeaderboardOutcome;
}

export interface RunSummaryInput {
  gameStatus: LeaderboardOutcome;
  compliance: number;
  budget: number;
  threat: number;
  day: number;
  tickNumber?: number;
  activeScenario?: Scenario | null;
  milestones: Milestone[];
  scenarioObjectives: ScenarioObjective[];
  score?: number;
  rank?: number;
  completionGrade?: string;
  completionHeadline?: string;
}

/** Composite score: compliance + budget/1000 - threat */
export function calculateLeaderboardScore(
  compliance: number,
  budget: number,
  threat: number,
): number {
  return Math.round((compliance + budget / 1000 - threat) * 100) / 100;
}

export function getLeaderboardKey(scenarioId?: string | null): string {
  return `${STORAGE_PREFIX}${scenarioId ?? 'sandbox'}`;
}

export function loadLeaderboard(scenarioId?: string | null): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(getLeaderboardKey(scenarioId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, LEADERBOARD_MAX_ENTRIES) as LeaderboardEntry[];
  } catch {
    return [];
  }
}

export function recordLeaderboardEntry(
  scenarioId: string | null | undefined,
  params: {
    compliance: number;
    budget: number;
    threat: number;
    day: number;
    outcome: LeaderboardOutcome;
  },
): { entry: LeaderboardEntry; leaderboard: LeaderboardEntry[]; rank: number } {
  const score = calculateLeaderboardScore(params.compliance, params.budget, params.threat);
  const entry: LeaderboardEntry = {
    score,
    date: new Date().toISOString(),
    playerLabel: 'You',
    compliance: params.compliance,
    budget: params.budget,
    threat: params.threat,
    day: params.day,
    outcome: params.outcome,
  };

  const existing = loadLeaderboard(scenarioId);
  const updated = [...existing, entry]
    .sort((a, b) => b.score - a.score)
    .slice(0, LEADERBOARD_MAX_ENTRIES);

  localStorage.setItem(getLeaderboardKey(scenarioId), JSON.stringify(updated));

  const rank = updated.findIndex((e) => e.date === entry.date) + 1;
  return { entry, leaderboard: updated, rank: rank > 0 ? rank : updated.length };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function outcomeLabel(status: LeaderboardOutcome): string {
  if (status === 'lost') return 'SYSTEM FAILURE';
  if (status === 'scenario_won') return 'MISSION ACCOMPLISHED';
  return 'IPO SUCCESS';
}

export function formatRunSummaryText(input: RunSummaryInput): string {
  const {
    gameStatus,
    compliance,
    budget,
    threat,
    day,
    tickNumber,
    activeScenario,
    milestones,
    scenarioObjectives,
    score,
    rank,
    completionGrade,
    completionHeadline,
  } = input;

  const completedMilestones = milestones.filter((m) => m.completed);
  const completedObjectives = scenarioObjectives.filter((o) => o.completed);
  const computedScore =
    score ?? calculateLeaderboardScore(compliance, budget, threat);
  const modeLine = activeScenario
    ? `Scenario: ${activeScenario.title}`
    : 'Mode: Sandbox';

  const lines: string[] = [
    'COMPLIANCE COMMAND — RUN SUMMARY',
    '================================',
    `Outcome: ${outcomeLabel(gameStatus)}`,
    modeLine,
    '',
    'STATS',
    `Compliance: ${Math.round(compliance)}%`,
    `Budget: ${formatCurrency(budget)}`,
    `Threat: ${Math.round(threat)}`,
    `Day: ${day}`,
  ];

  if (tickNumber !== undefined) {
    lines.push(`Ticks: ${tickNumber}`);
  }

  lines.push(
    '',
    `MILESTONES: ${completedMilestones.length}/${milestones.length} complete`,
  );

  if (completedMilestones.length > 0) {
    completedMilestones.forEach((m) => lines.push(`  ✓ ${m.name}`));
  }

  if (scenarioObjectives.length > 0) {
    lines.push(
      '',
      `OBJECTIVES: ${completedObjectives.length}/${scenarioObjectives.length} complete`,
    );
    scenarioObjectives.forEach((o) => {
      lines.push(`  ${o.completed ? '✓' : '○'} ${o.description}`);
    });
  }

  if (activeScenario && (gameStatus === 'scenario_won' || gameStatus === 'lost')) {
    const debrief = gameStatus === 'lost'
      ? (activeScenario.debriefLoss ?? activeScenario.historicalOutcome)
      : (activeScenario.debriefWin ?? activeScenario.historicalOutcome);
    if (debrief) {
      lines.push('', `Historical note: ${debrief}`);
    }
  }

  if (completionGrade) {
    lines.push('', `Completion grade: ${completionGrade}`);
    if (completionHeadline) {
      lines.push(completionHeadline);
    }
  }

  lines.push('', `Score: ${computedScore.toFixed(2)}`);
  if (rank !== undefined) {
    lines.push(`Rank on this device: #${rank}`);
  }
  lines.push('', 'Compliance Command v2.1 — Zero Trust');

  return lines.join('\n');
}